import type { CssGeneratorContext } from '../css-generator.model';

export const styles = ({ dt }: CssGeneratorContext) => `.account-identity-header {
  & .avatar {
    font-weight: ${dt('accountPage.identityHeader.avatar.fontWeight')};
  }

  & .name {
    color: ${dt('accountPage.identityHeader.name.color')};
    font-size: ${dt('accountPage.identityHeader.name.fontSize')};
    line-height: ${dt('accountPage.identityHeader.name.lineHeight')};
    font-weight: ${dt('accountPage.identityHeader.name.fontWeight')};
    margin: 0;
  }

  & .meta {
    color: ${dt('accountPage.identityHeader.meta.color')};
    font-size: ${dt('accountPage.identityHeader.meta.fontSize')};
    line-height: ${dt('accountPage.identityHeader.meta.lineHeight')};
    margin: 0;
  }

  & .chips .p-tag {
    font-size: ${dt('accountPage.identityHeader.chip.fontSize')};
    font-weight: ${dt('accountPage.identityHeader.chip.fontWeight')};
    padding: ${dt('accountPage.identityHeader.chip.padding')};
  }
}

@media screen and (max-width: 767px) {
  .account-identity-header .name {
    font-size: ${dt('accountPage.identityHeader.name.mobileFontSize')};
  }
}

.account-separator {
  display: inline-block;
  width: ${dt('accountPage.separator.size')};
  height: ${dt('accountPage.separator.size')};
  margin: 0 ${dt('accountPage.separator.gap')};
  border-radius: 50%;
  background: currentColor;
  vertical-align: middle;
}

.account-nav-sign-out {
  padding: ${dt('accountPage.navSignOut.padding')};
  border: 0;
  border-radius: ${dt('accountPage.navSignOut.borderRadius')};
  background: transparent;
  color: ${dt('accountPage.navSignOut.color')};
  font: inherit;
  font-size: ${dt('accountPage.navSignOut.fontSize')};
  line-height: ${dt('accountPage.navSignOut.lineHeight')};
  font-weight: ${dt('accountPage.navSignOut.fontWeight')};
  text-align: left;
  cursor: pointer;

  & .pi {
    color: inherit;
    font-size: ${dt('accountPage.navSignOut.iconSize')};
  }

  &:hover {
    background: ${dt('accountPage.navSignOut.hoverBackground')};
  }

  &:focus-visible {
    outline: ${dt('focus.ring.width')} ${dt('focus.ring.style')} ${dt('focus.ring.color')};
    outline-offset: ${dt('focus.ring.offset')};
  }
}

.account-page-card-header {
  & .title {
    color: ${dt('accountPage.cardHeader.title.color')};
    font-size: ${dt('accountPage.cardHeader.title.fontSize')};
    line-height: ${dt('accountPage.cardHeader.title.lineHeight')};
    font-weight: ${dt('accountPage.cardHeader.title.fontWeight')};
    margin: 0;
  }

  & .subtitle {
    color: ${dt('accountPage.cardHeader.subtitle.color')};
    font-size: ${dt('accountPage.cardHeader.subtitle.fontSize')};
    line-height: ${dt('accountPage.cardHeader.subtitle.lineHeight')};
    margin: 0;
  }
}

.account-workload-tile {
  background: ${dt('accountPage.workloadTile.background')};
  border: 1px solid ${dt('accountPage.workloadTile.borderColor')};
  border-radius: ${dt('accountPage.workloadTile.borderRadius')};
  padding: ${dt('accountPage.workloadTile.padding')};
  text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  & .icon {
    width: ${dt('accountPage.workloadTile.icon.size')};
    height: ${dt('accountPage.workloadTile.icon.size')};
    border-radius: ${dt('accountPage.workloadTile.icon.borderRadius')};
    background: ${dt('accountPage.workloadTile.icon.background')};
    color: ${dt('accountPage.workloadTile.icon.color')};
    font-size: ${dt('accountPage.workloadTile.icon.fontSize')};

    & .pi {
      font-size: inherit;
    }
  }

  & .count {
    color: ${dt('accountPage.workloadTile.count.color')};
    font-size: ${dt('accountPage.workloadTile.count.fontSize')};
    line-height: ${dt('accountPage.workloadTile.count.lineHeight')};
    font-weight: ${dt('accountPage.workloadTile.count.fontWeight')};
  }

  & .label {
    color: ${dt('accountPage.workloadTile.label.color')};
    font-size: ${dt('accountPage.workloadTile.label.fontSize')};
    line-height: ${dt('accountPage.workloadTile.label.lineHeight')};
    font-weight: ${dt('accountPage.workloadTile.label.fontWeight')};
  }

  & .chevron {
    color: ${dt('accountPage.workloadTile.chevron.color')};
    font-size: ${dt('accountPage.workloadTile.chevron.fontSize')};
    transition: color ${dt('transition.duration')}, transform ${dt('transition.duration')};
  }

  &[data-empty] {
    & .count {
      color: ${dt('accountPage.workloadTile.empty.countColor')};
    }

    & .icon {
      background: ${dt('accountPage.workloadTile.empty.iconBackground')};
      color: ${dt('accountPage.workloadTile.empty.iconColor')};
    }
  }
}

@media screen and (max-width: 639px) {
  .account-workload-tile {
    padding: ${dt('accountPage.workloadTile.mobilePadding')};
  }
}

a.account-workload-tile:hover {
  border-color: ${dt('accountPage.workloadTile.hoverBorderColor')};
  box-shadow: ${dt('accountPage.workloadTile.hoverShadow')};
}

a.account-workload-tile:focus-visible {
  outline: ${dt('focus.ring.width')} ${dt('focus.ring.style')} ${dt('focus.ring.color')};
  outline-offset: ${dt('focus.ring.offset')};
}

a.account-workload-tile:hover .chevron,
a.account-workload-tile:focus-visible .chevron {
  color: ${dt('accountPage.workloadTile.chevron.hoverColor')};
  transform: translateX(${dt('accountPage.workloadTile.chevron.hoverShift')});
}

.account-preference-row {
  padding: ${dt('accountPage.preferenceRow.padding')};

  & + .account-preference-row {
    border-top: 1px solid ${dt('accountPage.preferenceRow.borderColor')};
  }

  &:last-child {
    padding-bottom: 0;
  }

  & .label {
    color: ${dt('accountPage.preferenceRow.label.color')};
    font-weight: ${dt('accountPage.preferenceRow.label.fontWeight')};
  }

  & .description {
    color: ${dt('accountPage.preferenceRow.description.color')};
    font-size: ${dt('accountPage.preferenceRow.description.fontSize')};
    line-height: ${dt('accountPage.preferenceRow.description.lineHeight')};
  }
}

.account-signature-preview {
  background: ${dt('accountPage.signaturePreview.background')};
  border: 1px solid ${dt('accountPage.signaturePreview.borderColor')};
  border-radius: ${dt('accountPage.signaturePreview.borderRadius')};
  padding: ${dt('accountPage.signaturePreview.padding')};
  margin: 0;

  & img {
    display: block;
    max-width: min(${dt('accountPage.signaturePreview.image.maxWidth')}, 100%);
    height: auto;
  }

  & figcaption {
    border-top: 1px solid ${dt('accountPage.signaturePreview.lineColor')};
    padding-top: ${dt('accountPage.signaturePreview.caption.paddingTop')};
    color: ${dt('accountPage.signaturePreview.caption.color')};
    font-size: ${dt('accountPage.signaturePreview.caption.fontSize')};
    line-height: ${dt('accountPage.signaturePreview.caption.lineHeight')};
    text-align: center;
  }
}

.account-signature-empty {
  border: 1px dashed ${dt('accountPage.signatureEmpty.borderColor')};
  border-radius: ${dt('accountPage.signatureEmpty.borderRadius')};
  padding: ${dt('accountPage.signatureEmpty.padding')};
  text-align: center;

  & .icon {
    width: ${dt('accountPage.signatureEmpty.icon.size')};
    height: ${dt('accountPage.signatureEmpty.icon.size')};
    border-radius: 50%;
    background: ${dt('accountPage.signatureEmpty.icon.background')};
    color: ${dt('accountPage.signatureEmpty.icon.color')};
    font-size: ${dt('accountPage.signatureEmpty.icon.fontSize')};

    & .pi {
      font-size: inherit;
    }
  }

  & .title {
    color: ${dt('accountPage.signatureEmpty.title.color')};
    font-size: ${dt('accountPage.signatureEmpty.title.fontSize')};
    line-height: ${dt('accountPage.signatureEmpty.title.lineHeight')};
    font-weight: ${dt('accountPage.signatureEmpty.title.fontWeight')};
    margin: 0;
  }

  & .description {
    color: ${dt('accountPage.signatureEmpty.description.color')};
    font-size: ${dt('accountPage.signatureEmpty.description.fontSize')};
    line-height: ${dt('accountPage.signatureEmpty.description.lineHeight')};
    margin: 0;
  }
}
`;
